import React, { Component, Fragment } from "react";
import clsx from "clsx";

import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  Cell,
  ReferenceDot,
} from "recharts";

import {
  selectFormat,
  MAIN_COLORS,
  CustomizedYAxisLabel,
  StandardTooltip,
  Error,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  RenderBackground,
  InsightsTickIndicator,
  closeAllBarchartTooltips,
} from "components/Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XrayUtils.js";

import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

class HearstTopCreatives extends Component {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };
    this.divPosition = React.createRef();

    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  clickBar(d) {
    if (this.state.tooltip.show && d.date === this.state.tooltip.date) {
      this.setState({
        tooltip: { ...d, show: false },
      });
    } else {
      this.setState({
        tooltip: { ...d, show: true },
      });
    }
  }

  closeTooltip() {
    this.setState({
      tooltip: { show: false },
    });
  }

  handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  renderBars = (data) => {
    let bars = [];
    let all_bars = [];
    let labels = [];

    const sortedKeys = this.getSortedKeys(data);
    const insights = this.state.widgetsInsights;

    for (let i = 0; i <= data.length - 1; i++) {
      sortedKeys.forEach((source) => all_bars.push(source));
    }

    const isFixedBarWidth = data.length <= 7;

    [...new Set(all_bars)].forEach((bar, i) => {
      bars.push(
        <Bar
          key={i}
          dataKey={bar}
          stackId="stack"
          barSize={isFixedBarWidth ? 35 : null}
          fill={MAIN_COLORS[i]}
          isAnimationActive={false}
          cursor="pointer"
          // only first bar needs background
          background={
            i === 0 ? (
              <RenderBackground
                insights={insights}
                showIndicator={!this.state.tooltip.show}
                node={this.node}
                i={i}
              />
            ) : null
          }
          onClick={(d) => {
            const label = labels.filter((l) => l.date === d.date);
            const extra = sortedKeys.map((k, j) => ({
              key: k,
              value: d[k],
              color: MAIN_COLORS[j],
            }));

            d.extra = extra;
            d.insight = label.length ? label[0] : null;

            this.clickBar(d, bar);
          }}
        >
          {data.map(
            (entry, index) =>
              entry && (
                <Cell
                  key={`cell-${bar}-${index}`}
                  fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                />
              )
          )}
        </Bar>
      );
    });

    return bars;
  };

  legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="legend mt-2 mt-md-0">
        <span className="legend__icon" style={{ background: MAIN_COLORS[i] }} />
        <span className="legend__text" style={{ color: MAIN_COLORS[i] }}>
          {entry.replace("_", " ")}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    let legends = [];
    let all_legends = [];

    const sortedKeys = this.getSortedKeys(data);

    for (let i = 0, j = data.length; i <= j - 1; i++) {
      sortedKeys.forEach((source) => all_legends.push(source));
    }

    legends = [...new Set(all_legends)];
    return this.legendElement(legends);
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);

    const keys = Object.keys(data[0]).filter(
      (key) => !["target_audience"].includes(key)
    );
    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  }

  getExportData = () => ({
    data: this.props.data.data,

    opts: {
      type: "chart",
      date: {
        name: "target_audience",
        no_date: true,
      },
    },
  });

  render() {
    const { data, title } = this.props;
    const { widgetsInsights } = this.state;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    const sortedData = this.props.data.data.sort(
      (a, b) => b.video_impressions - a.video_impressions
    );

    return (
      <Fragment>
        <div ref={this.divPosition}>
          {this.renderLegends(sortedData)}
          <div className="d-flex mt-2">
            <ResponsiveContainer height={350} width="100%">
              <BarChart
                data={sortedData}
                layout="vertical"
                margin={{ top: 12, right: 0, bottom: 20, left: 60 }}
                onMouseOver={(e) => CloseSelectedInsight(this.node)}
                onMouseOut={(e) =>
                  OpenSelectedInsight(this.node, !this.state.tooltip.show)
                }
              >
                <CartesianGrid
                  stroke="#FEE5DE"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                {!this.state.tooltip.show && (
                  <Tooltip
                    cursor={{ fill: "#eee", opacity: 0.5 }}
                    content={
                      <StandardTooltip
                        render={({ data }) => {
                          const payload = data(this.props);

                          if (payload !== null) {
                            const tooltip_data = payload[0].payload;

                            return (
                              <div className="standard-tooltip">
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Target Audience:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.target_audience}
                                  </span>
                                </header>

                                <main className="standard-tooltip__main">
                                  {Object.keys(payload)
                                    .sort(
                                      (a, b) =>
                                        payload[b].value - payload[a].value
                                    )
                                    .map((key) => (
                                      <div
                                        className="standard-tooltip__item"
                                        key={key}
                                      >
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[key].color,
                                          }}
                                        />
                                        <span
                                          className={clsx(
                                            "standard-tooltip__key",
                                            "text-capitalize"
                                          )}
                                          style={{ color: payload[key].color }}
                                        >
                                          {payload[key].dataKey.replace(
                                            "_",
                                            " "
                                          )}{" "}
                                        </span>
                                        <span className="standard-tooltip__value">
                                          {selectFormat(
                                            "number",
                                            payload[key].value
                                          )}
                                        </span>
                                      </div>
                                    ))}
                                </main>
                              </div>
                            );
                          }
                        }}
                      />
                    }
                  />
                )}
                {this.renderBars(sortedData)}

                <XAxis
                  type="number"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(d) => selectFormat("number", d)}
                  scale="auto"
                />
                <YAxis
                  dataKey="target_audience"
                  type="category"
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  tick={<CustomizedYAxisLabel />}
                />
                {/* Adds insight icon under x-axis ticks  */}
                {insightsData &&
                  widgetsInsights.map((o, index) => (
                    <ReferenceDot
                      x={o.date}
                      y={0}
                      key={title + index}
                      ifOverflow="extendDomain"
                      shape={
                        <InsightsTickIndicator
                          insights={widgetsInsights}
                          date={o.date}
                          node={this.node}
                        />
                      }
                    />
                  ))}
              </BarChart>
            </ResponsiveContainer>
            <div className="featured-insight-tooltip" ref={this.node}>
              {widgetsInsights.length > 0 && !this.state.tooltip.show && (
                <RenderTooltipInsights data={widgetsInsights} />
              )}
            </div>
          </div>
          <div
            className="xray-tooltip-container"
            style={{
              top: "5.4rem",
              left:
                this.state.tooltip.x + this.state.tooltip.width + 220 <
                this.state.width
                  ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                  : this.state.tooltip.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              insights={widgetsInsights}
              close={this.closeTooltip.bind(this)}
            />
          </div>
        </div>
      </Fragment>
    );
  }
}

export default HearstTopCreatives;
